import {
    Component,
    ChangeDetectorRef,
    NgZone,
    OnDestroy,
    OnInit,
    Input,
    ViewChildren,
    QueryList,
    Output,
    ElementRef,
    EventEmitter,
    AfterViewInit,
    ViewChild
} from '@angular/core';

import { Subscription, take, debounceTime } from 'rxjs';
import { DndDropEvent } from 'ngx-drag-drop';

import { AbstractPageBuilderService } from '../../services/abstract-page-builder.service';
import { BlockModel } from '../../models/block.model';
import { BlockWrapperComponent } from '../BlockWrapper/block-wrapper.component';
import { GridsterConfig } from 'angular-gridster2';
import { BlockTypeModel } from '../../models/block-type.model';
import { LayoutBlock } from '../../models/layout-block.model';
import { CuratorBlock } from '../../models/curator-block.model';
import { LayoutTypesEnum } from '@Common/Layout/enums/layout-types.enum';
import { PanelPositionEnum } from '@Common/Forms/components/KebabMenu/kebab-menu.component';
import { MenuItem } from 'primeng/api';
import { BlockTypeGroupModel } from '@Common/PageBuilder/models/block-type-group.model';

@Component({
    selector: 'agpr-page-builder',
    templateUrl: './page-builder.component.html',
    styleUrls: ['./page-builder.component.scss'],
    standalone: false
})
export class PageBuilderComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() public styleClass: string;
    @Input() showPlaceholder: boolean = false;
    @Input() inlineAdderAdjustment: boolean;
    @Input() actionMenuTextAlignment: string;

    /** List of wrapper components for comparison when the list changes */
    mostRecentWrappers: ElementRef[];
    /** List of wrapper components currently in the DOM */
    @ViewChildren(BlockWrapperComponent, {read: ElementRef}) blockWrappers: QueryList<ElementRef>;
    /**
     * Outputs a reference to the DOM Element when a block is added
     * NOTE: Only emits when a single block was added
     */
    @Output() blockAdded: EventEmitter<ElementRef> = new EventEmitter<ElementRef>();

    @Output() blockMovedByKeyboard: EventEmitter<{event: KeyboardEvent; block: BlockModel}> = new EventEmitter<{event: KeyboardEvent; block: BlockModel}>();

    @Output() blockResizedByKeyboard: EventEmitter<{event: KeyboardEvent; block: BlockModel}> = new EventEmitter<{event: KeyboardEvent; block: BlockModel}>();

    /** Container of the contents of the page builder so we can find things inside */
    @ViewChild('container') container: ElementRef;

    private subscriptions: Subscription[] = [];
    public blocks: BlockModel[] = [];
    public loading: boolean = false;

    public isGrid: boolean = false;
    public gridsterOptions: GridsterConfig = {};
    draggingBlock: BlockModel;
    draggingBlockType: BlockTypeModel;
    draggingBlockPosition: string;

    placeholderAdderVisible: boolean = false;
    isDragging: boolean = false;
    readonly panelPositions = PanelPositionEnum;
    optionsCenter: MenuItem[] = [];
    blockTypeGroups: BlockTypeGroupModel[];
    isRightMost: boolean = false;

    private dragScrollRafId: number;
    private dragClientY: number = -1;
    private dropHandled = false;
    private isDraggingTimerId: ReturnType<typeof setTimeout> | null = null;
    private _dragCursorHandler: ((e: DragEvent) => void) | null = null;

    constructor(
        public pageBuilderService: AbstractPageBuilderService,
        public changeDetector: ChangeDetectorRef,
        private ngZone: NgZone
    ) {
        if (this.pageBuilderService.isGrid) {
            this.isGrid = true;
            this.gridsterOptions = this.pageBuilderService.gridSettings || {};
            this.wrapGridsterItemChangeCallback();
        } else {
            this.isGrid = false;
            this.gridsterOptions = null;
        }
    }

    /**
     * Wraps the gridster itemChangeCallback so that change detection is triggered
     * after gridster completes a move or resize operation. This is necessary because
     * the page-builder detaches from Angular's change detection for performance.
     */
    private wrapGridsterItemChangeCallback(): void {
        const originalCallback = this.gridsterOptions.itemChangeCallback;
        this.gridsterOptions.originalItemChange = originalCallback;
        this.gridsterOptions.itemChangeCallback = (item, itemComponent) => {
            if (originalCallback) {
                originalCallback(item, itemComponent);
            }
            // Defer detectChanges so that gridster finishes its entire layout pass
            // calling detectChanges synchronously here causes Angular to re-render mid-pass
            setTimeout(() => this.changeDetector.detectChanges());
        };
    }

    ngOnInit () {
        this.subscriptions.push(
            this.pageBuilderService.getBlocks().subscribe(
                (blocks) => {
                    if (blocks) {
                        this.blocks = blocks;
                    } else {
                        this.blocks = [];
                    }
                    this.loading = !blocks;
                    this.changeDetector.detectChanges();

                    if (this.isGrid) {
                        setTimeout(() => this.changeDetector.detectChanges());
                    }
                }
            ),
            this.pageBuilderService.getDraggingBlock().subscribe(block => {
                this.draggingBlock = block;
                this.changeDetector.detectChanges();
            }),
            this.pageBuilderService.getDraggingBlockType().subscribe(blockType => {
                this.draggingBlockType = blockType;
                this.changeDetector.detectChanges();
            }),
            this.pageBuilderService.getDraggingBlockPosition().subscribe(blockPosition => {
                this.draggingBlockPosition = blockPosition;
                this.changeDetector.detectChanges();
            })
        );
        this.pageBuilderService.getBlockTypeGroups().subscribe(blockTypeGroups => {
            this.blockTypeGroups = blockTypeGroups;
            this.setBlockAdderOptions();
        });
    }

    ngAfterViewInit() {
        this.changeDetector.detach();
        this.ngZone.runOutsideAngular(() => {
            const isInSidePanel = (el: EventTarget | null): boolean =>
                !!(el as Element)?.closest?.('.agpr-leftSidebar, .agpr-rightSidebar');

            this._dragCursorHandler = (e: DragEvent) => {
                if (isInSidePanel(e.target)) {
                    return;
                }
                if ((e.target as Element)?.closest?.('.agpr-pageBuilder-dropzone')) {
                    return;
                }
                e.preventDefault();
                if (e.dataTransfer) {
                    e.dataTransfer.dropEffect = 'move';
                }
            };
            document.addEventListener('dragenter', this._dragCursorHandler, true);
            document.addEventListener('dragover', this._dragCursorHandler, true);
        });
        this.subscriptions.push(
            this.blockWrappers.changes.subscribe(
                // It just gives the list instead of what the changes are
                (change: QueryList<ElementRef>) => {
                    if (!this.mostRecentWrappers) {
                        this.mostRecentWrappers = change.toArray().map(ref => ref.nativeElement);
                        return;
                    }
                    const newBlocks = change.filter(blockRef => !this.mostRecentWrappers.includes(blockRef.nativeElement));

                    if (newBlocks.length === 1) {
                        this.blockAdded.emit(newBlocks[0]);
                    }
                    this.mostRecentWrappers = change.toArray().map(ref => ref.nativeElement);
                    this.changeDetector.detectChanges();
                }
            ),
            this.pageBuilderService.getBlockDisplayElementChanged().pipe(debounceTime(300)).subscribe(() => {
                this.changeDetector.detectChanges();
            })
        );
    }

    ngOnDestroy() {
        this.changeDetector.reattach();
        this.subscriptions.forEach((value) => {
            value.unsubscribe();
        });

        cancelAnimationFrame(this.dragScrollRafId);
        if (this.isDraggingTimerId !== null) {
            clearTimeout(this.isDraggingTimerId);
        }
        if (this._dragCursorHandler) {
            document.removeEventListener('dragenter', this._dragCursorHandler, true);
            document.removeEventListener('dragover', this._dragCursorHandler, true);
        }
        // remove the item change wrapper when page builder is destroyed, so that if a new one is created, it doesn't further wrap it
        if (this.gridsterOptions) {
            this.gridsterOptions.itemChangeCallback = this.gridsterOptions.originalItemChange;
        }
    }

    /** Gets all of the error elements from inside of page builder */
    getErrorElements(): HTMLElement[] {
        return Array.from(this.container.nativeElement.querySelectorAll('.agpr-blockError'));
    }

    focusFirstBlockWithErrors() {
        const errors = this.getErrorElements();
        if (errors?.length && errors[0]) {
            const idToFocus = errors[0].id.slice(0, errors[0].id.indexOf('_error'));
            document.querySelector<HTMLElement>(`button[id^=${idToFocus}]`)?.focus();
        }
    }

    /**
     * Handles the event when a block is dropped into a new position in the block list: aka at the end of the drag & drop
     */
    onDropBlocks(event: DndDropEvent): void {
        this.placeholderAdderVisible = false;
        if (event.type === 'block') {
            let index = event.index;
            let appendToEnd = false;

            if (index === undefined || index === null) {
                if (event.dropEffect !== 'move' || this.dragClientY < 0) {
                    return;
                }
                // Infer position from where the drag ended on screen
                if (this.dragClientY < window.innerHeight / 2) {
                    index = 0;
                } else {
                    appendToEnd = true;
                    index = 0;
                }
            }

            if (event.dropEffect === 'move') {
                const blocks = this.blocks.slice();
                const indexOfBlock = blocks.indexOf(this.draggingBlock);
                let fromLayoutBlock = false;
                if (indexOfBlock > -1) {
                    blocks.splice(indexOfBlock, 1);
                } else {
                    if (this.draggingBlockPosition) {
                        // If draggingBlockPosition is defined, then this block is dragged out of a layout block
                        fromLayoutBlock = true;
                        const indexParts = this.draggingBlockPosition.split('.');
                        const blockPosition = +indexParts[0];
                        const columnIndex = +indexParts[1];
                        const positionInColumn = +indexParts[2];
                        let column;
                        if (this.blocks[blockPosition] instanceof LayoutBlock) {
                            column = (this.blocks[blockPosition] as LayoutBlock).blocks[columnIndex];
                        } else {
                            // Curator block
                            column = (this.blocks[blockPosition] as CuratorBlock).blocks;
                            this.pageBuilderService.moveOutCuratorBlock(this.draggingBlock);
                        }
                        column.splice(positionInColumn, 1);
                    } else {
                        blocks.forEach(block => {
                            if (!(block as any).blocks) {
                                return;
                            }
                            const layoutBlock = block as LayoutBlock;
                            layoutBlock.blocks.forEach(column => {
                                const idx = column.indexOf(this.draggingBlock);
                                column.splice(idx, 1);
                            });
                        });
                    }
                }

                if (appendToEnd) {
                    blocks.push(this.draggingBlock);
                } else {
                    if (index > indexOfBlock && indexOfBlock > -1) {
                        index--;
                    }

                    // check to see if the block is being moved passed a movable block
                    if (this.blocks[index] && this.blocks[index].canMove) {
                        blocks.splice(index, 0, this.draggingBlock);
                    } else {
                        // else the block is trying to pass a non movable block

                        // if is at index 0, move block under the non movable block (handle wire default text widget)
                        if (index === 0) {
                            blocks.splice(index + 1, 0, this.draggingBlock);

                        // else move block before the non movable block (handle distribution footer)
                        } else {
                            if (fromLayoutBlock) {
                                blocks.splice(index, 0, this.draggingBlock);
                            } else {
                                blocks.splice(index - 1, 0, this.draggingBlock);
                            }
                        }
                    }
                }
                this.pageBuilderService.setBlocks(blocks);
                this.dropHandled = true;
            }
        } else if (event.type === 'blockType') {
            this.pageBuilderService.addBlockOfType(this.draggingBlockType, event.index + '');
        }
    }
    /**
     * Handles starting a drag event for blocks
     */
    onDragStartBlocks(block: BlockModel, event: any): void {
        this.placeholderAdderVisible = false;
        this.draggingBlock = block;
        this.pageBuilderService.setDraggingBlock(block);
        this.pageBuilderService.setDraggingBlockType(block.type);

        if (event?.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.dropEffect = 'move';
        }

        this.isDraggingTimerId = setTimeout(() => {
            this.isDraggingTimerId = null;
            this.isDragging = true;
            this.changeDetector.detectChanges();
        });

        this.ngZone.runOutsideAngular(() => {
            const sourceEl = event?.target as HTMLElement;
            const onDrag = (e: DragEvent) => {
                if (e.clientY > 0) {
                    this.dragClientY = e.clientY;
                }
            };
            document.addEventListener('dragover', onDrag, true);
            sourceEl?.addEventListener('drag', onDrag);
            const scrollContainer = (() => {
                let el = this.container.nativeElement.parentElement;
                while (el && el !== document.body) {
                    const ov = getComputedStyle(el).overflowY;
                    if ((ov === 'auto' || ov === 'scroll') && el.scrollHeight > el.clientHeight) {
                        return el;
                    }
                    el = el.parentElement;
                }
                return document.scrollingElement || document.documentElement;
            })();
            const loop = () => {
                const y = this.dragClientY;
                const vh = window.innerHeight;
                if (y > 0 && y < 200) {
                    scrollContainer.scrollTop -= Math.round((1 - y / 200) * 25);
                } else if (y > vh - 200) {
                    scrollContainer.scrollTop += Math.round(((y - (vh - 200)) / 200) * 25);
                }
                this.dragScrollRafId = requestAnimationFrame(loop);
            };
            this.dragScrollRafId = requestAnimationFrame(loop);
            sourceEl?.addEventListener('dragend', () => {
                document.removeEventListener('dragover', onDrag, true);
                sourceEl?.removeEventListener('drag', onDrag);
                cancelAnimationFrame(this.dragScrollRafId);
            }, { once: true });
        });
    }

    /**
     * Cleans up after a drag has been completed
     *
     * @return void
     */
    public cleanupAfterDragCompleted(event: any): void {
        if (this.isDraggingTimerId !== null) {
            clearTimeout(this.isDraggingTimerId);
            this.isDraggingTimerId = null;
        }
        this.isDragging = false;
        if (!this.dropHandled && this.draggingBlock) {
            const blockIndex = this.blocks.indexOf(this.draggingBlock);
            if (blockIndex > -1 && this.dragClientY > 0) {
                const blocks = this.blocks.slice();
                blocks.splice(blockIndex, 1);

                const dropzone = this.container.nativeElement.querySelector('.agpr-pageBuilder-dropzone');
                const blockEls = Array.from(
                    dropzone?.querySelectorAll(':scope > div:not(.agpr-drag-placeholder)') ?? []
                ) as HTMLElement[];

                // Find the first block whose vertical midpoint is below the cursor —
                // that becomes the insertion point. Defaults to end if cursor is
                // below all blocks (or they are all off-screen above).
                let targetIndex = blockEls.length;
                for (let i = 0; i < blockEls.length; i++) {
                    const r = blockEls[i].getBoundingClientRect();
                    if (this.dragClientY < r.top + r.height / 2) {
                        targetIndex = i;
                        break;
                    }
                }

                if (targetIndex > blockIndex) {
                    targetIndex--;
                }

                let finalIndex: number;
                if (targetIndex >= blocks.length) {
                    finalIndex = blocks.length;
                    blocks.push(this.draggingBlock);
                } else if (blocks[targetIndex]?.canMove) {
                    finalIndex = targetIndex;
                    blocks.splice(targetIndex, 0, this.draggingBlock);
                } else if (targetIndex === 0) {
                    finalIndex = 1;
                    blocks.splice(1, 0, this.draggingBlock);
                } else {
                    finalIndex = targetIndex - 1;
                    blocks.splice(targetIndex - 1, 0, this.draggingBlock);
                }
                this.pageBuilderService.setBlocks(blocks);

                setTimeout(() => {
                    const updatedEls = Array.from(
                        dropzone?.querySelectorAll(':scope > div:not(.agpr-drag-placeholder)') ?? []
                    ) as HTMLElement[];
                    updatedEls[finalIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                });
            }
        }
        if (this.dropHandled && this.draggingBlock) {
            const blockToScroll = this.draggingBlock;
            setTimeout(() => {
                const index = this.blocks.indexOf(blockToScroll);
                if (index < 0) {
                    return;
                }
                const dropzone = this.container.nativeElement.querySelector('.agpr-pageBuilder-dropzone');
                const blockEls = Array.from(
                    dropzone?.querySelectorAll(':scope > div:not(.agpr-drag-placeholder)') ?? []
                ) as HTMLElement[];
                blockEls[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            });
        }
        this.dropHandled = false;
        this.draggingBlock = null;
        setTimeout(() => {
            if (event?.target?.style) {
                event.target.style.pointerEvents = 'unset';
            }
        }, 500);
        this.pageBuilderService.setDraggingBlock(null);
        this.pageBuilderService.setDraggingBlockType(null);
    }

    onKeydown(event: KeyboardEvent, block: BlockModel) {
        if ((event.target as HTMLElement).classList.contains('gridster-item-resize')) {
            this.blockResizedByKeyboard.emit({event: event, block: block});
        } else {
            this.blockMovedByKeyboard.emit({event: event, block: block});
        }
    }

    setPlaceholder(placeholderAdderVisible) {
        this.placeholderAdderVisible = placeholderAdderVisible;
        this.changeDetector.detectChanges();
    }

    setBlockAdderOptions(): void {
        this.optionsCenter = [];

        const componentOptions = {
            label: 'PageBuilder.inlineAdder.components',
            items: [],
            style: {
                alignmentPriority: LayoutTypesEnum.horizontal,
                panelPositionX: this.isRightMost ? PanelPositionEnum.left : PanelPositionEnum.right
            }
        };

        const layoutOptions = {
            label: 'PageBuilder.inlineAdder.layouts',
            items: [],
            style: {
                alignmentPriority: LayoutTypesEnum.horizontal,
                panelPositionX: this.isRightMost ? PanelPositionEnum.left : PanelPositionEnum.right
            }
        };

        if (this.blockTypeGroups?.length > 0) {
            this.blockTypeGroups.forEach(blockGroup => {
                blockGroup.blockTypes.filter(x => !!x.showInBlockAdder).forEach(blockType => {
                    if (blockType.canAdd) {
                        componentOptions.items.push({
                            label: blockType.label,
                            icon: blockType.icon,
                            isLocked: blockType.isLocked,
                            command: (menuItemEvent) => {
                                this.pageBuilderService.addBlockOfTypeWithEvent(blockType, '0', menuItemEvent?.originalEvent);
                            }
                        });
                    }
                });
            });
        }

        this.pageBuilderService.getLayoutBlockTypes().pipe(take(1))
            .subscribe((layoutBlocks) => {
                layoutBlocks?.filter(x => !!x.showInBlockAdder).forEach(layout => {
                    switch (layout.key.toLowerCase()) {
                        case 'one_column_layout':
                            layout.icon = 'fa-duotone fa-regular fa-rectangle';
                            break;
                        case 'two_column_layout':
                            layout.icon = 'fa-kit-duotone fa-agpr-columns-2';
                            break;
                        case 'three_column_layout':
                            layout.icon = 'fa-duotone fa-regular fa-columns-3';
                            break;
                    }
                    if (layout.canAdd) {
                        layoutOptions.items.push({
                            label: layout.label,
                            icon: layout.icon,
                            isLocked: layout.isLocked,
                            command: (menuItemEvent) => {
                                this.pageBuilderService.addBlockOfTypeWithEvent(layout, '0', menuItemEvent?.originalEvent);
                            }
                        });
                    }
                });

                if (componentOptions.items?.length && layoutOptions.items.length) {
                    this.optionsCenter.push(componentOptions, layoutOptions);
                }
            });
    }
}
